import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchProperties, createProperty, updateProperty, deleteProperty,
  fetchAllPages,
} from '../api';
import { useConfirm } from './ConfirmProvider';
import { usePagination } from '../hooks/usePagination';
import PaginationBar from './shared/PaginationBar';

const TENURE_LABELS = {
  FREEHOLD: 'Freehold',
  TRIBAL: 'Tribal Land',
  LEASEHOLD: 'Leasehold',
  SECTIONAL: 'Sectional Title',
  COR: 'Certificate of Rights',
};

const MARITAL_LABELS = {
  IN_COP: 'In Community of Property',
  OUT_COP: 'Out of COP (ANC)',
  OUT_COP_ACCRUAL: 'Out of COP with Accrual',
  SINGLE: 'Single / N/A',
  TRIBAL: 'Married by Customary Law',
  UNKNOWN: 'Unknown',
};

const BLANK = {
  plot_number: '', district: '', township: '', physical_address: '',
  extent_sqm: '', tenure_type: 'FREEHOLD',
  deed_of_transfer_number: '', deed_registration_date: '',
  parent_title: '',
  registered_owner_client: null,
  registered_owner_name: '', registered_owner_id_number: '',
  registered_owner_marital_regime: 'UNKNOWN',
  existing_bond_holder: '', existing_bond_amount: '', existing_bond_reference: '',
  servitudes: '', caveats: '', notes: '',
};

export default function PropertyManagement({ authAxios, showFormMessage }) {
  const confirm = useConfirm();
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchProperties(authAxios);
      setProperties(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage?.('error', 'Failed to load properties.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, showFormMessage]);

  const loadClients = useCallback(async () => {
    try {
      // fetchAllPages walks every backend page — a plain single GET here
      // (docs/audits/pagination-audit-2026-08-13.md) silently showed only
      // the first 10 clients in the owner-client picker.
      const data = await fetchAllPages(authAxios, 'clients/');
      setClients(Array.isArray(data) ? data : []);
    } catch {
      // Non-fatal — picker just shows empty list
    }
  }, [authAxios]);

  useEffect(() => { load(); loadClients(); }, [load, loadClients]);

  // Auto-fill the legacy free-text fields from a selected client so the
  // FIA chain (already captured against that Client) is mirrored on the
  // property record. The user can still override afterwards.
  const handleSelectOwnerClient = (clientId) => {
    const cid = clientId ? Number(clientId) : null;
    if (!cid) {
      setForm(f => ({ ...f, registered_owner_client: null }));
      return;
    }
    const c = clients.find(x => x.id === cid);
    if (!c) {
      setForm(f => ({ ...f, registered_owner_client: cid }));
      return;
    }
    setForm(f => ({
      ...f,
      registered_owner_client: cid,
      // Auto-fill only if the user hasn't typed anything yet. Field names
      // must match ClientSerializer's actual output (legal_ops/serializers.py)
      // — id_number/tax_registration_number/display_name don't exist on the
      // API response, which silently broke this for Owner ID Number before.
      registered_owner_name: f.registered_owner_name || (c.first_name + ' ' + (c.last_name || '')).trim() || c.organization_name || '',
      registered_owner_id_number: f.registered_owner_id_number || c.id_or_passport_number || c.tax_identification_number || '',
    }));
  };

  const openCreate = () => { setEditing(null); setForm(BLANK); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...BLANK, ...p }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.extent_sqm) payload.extent_sqm = null;
      if (!payload.existing_bond_amount) payload.existing_bond_amount = null;
      if (!payload.deed_registration_date) payload.deed_registration_date = null;

      if (editing) {
        await updateProperty(authAxios, editing.id, payload);
        showFormMessage?.('success', 'Property updated.');
      } else {
        await createProperty(authAxios, payload);
        showFormMessage?.('success', 'Property created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    const ok = await confirm({ title: 'Delete property?', message: `Delete ${p.plot_number}? This cannot be undone.` });
    if (!ok) return;
    try {
      await deleteProperty(authAxios, p.id);
      showFormMessage?.('success', 'Property deleted.');
      load();
    } catch {
      showFormMessage?.('error', 'Failed to delete property.');
    }
  };

  const filtered = properties.filter(p =>
    [p.plot_number, p.district, p.registered_owner_name, p.deed_of_transfer_number]
      .join(' ').toLowerCase().includes(search.toLowerCase())
  );
  const { pageItems: pagedProperties, paginationProps } = usePagination(filtered);

  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Property Register</h2>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          + New Property
        </button>
      </div>

      <input
        type="text"
        placeholder="Search by plot number, district, owner, deed number…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                {['Plot Number', 'District / Township', 'Tenure', 'Deed No.', 'Registered Owner', 'Bond Holder', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedProperties.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No properties found.</td></tr>
              )}
              {pagedProperties.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{p.plot_number}</td>
                  <td className="px-4 py-3 text-gray-600">{[p.district, p.township].filter(Boolean).join(' / ')}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                      {TENURE_LABELS[p.tenure_type] ?? p.tenure_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{p.deed_of_transfer_number || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {p.registered_owner_display ?? p.registered_owner_name ?? '—'}
                    {p.registered_owner_client && (
                      <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700" title="Linked to client record">FIA ✓</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{p.existing_bond_holder || '—'}</td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => openEdit(p)} className="text-blue-600 hover:underline text-xs">Edit</button>
                    <button onClick={() => handleDelete(p)} className="text-red-500 hover:underline text-xs">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar {...paginationProps} emptyLabel="No properties found." />
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editing ? 'Edit Property' : 'New Property'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSave} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Plot Number *</label>
                <input required value={form.plot_number} onChange={e => fld('plot_number', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              {[
                ['District', 'district'], ['Township', 'township'],
              ].map(([label, key]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                  <input value={form[key]} onChange={e => fld(key, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Physical Address</label>
                <textarea value={form.physical_address} onChange={e => fld('physical_address', e.target.value)}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Extent (m²)</label>
                <input type="number" value={form.extent_sqm} onChange={e => fld('extent_sqm', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tenure Type</label>
                <select value={form.tenure_type} onChange={e => fld('tenure_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(TENURE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Deed of Transfer No.</label>
                <input value={form.deed_of_transfer_number} onChange={e => fld('deed_of_transfer_number', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Deed Registration Date</label>
                <input type="date" value={form.deed_registration_date} onChange={e => fld('deed_registration_date', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div className="md:col-span-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <label className="block text-xs font-medium text-blue-900 mb-1">
                  Link to Existing Client (recommended) — preserves FIA / KYC due-diligence chain
                </label>
                <select
                  value={form.registered_owner_client || ''}
                  onChange={e => handleSelectOwnerClient(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">— No linked client (use free-text fields below) —</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.display_name || c.first_name + ' ' + (c.last_name || '') || c.organization_name || `Client #${c.id}`}
                      {c.client_type ? ` · ${c.client_type}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-blue-700 mt-1">
                  When a client is linked, FIA documents captured on their record automatically apply to this property.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Registered Owner Name {form.registered_owner_client && <span className="text-blue-600">(from client)</span>}
                </label>
                <input value={form.registered_owner_name} onChange={e => fld('registered_owner_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Owner ID Number (Omang)</label>
                <input value={form.registered_owner_id_number} onChange={e => fld('registered_owner_id_number', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Owner Marital Regime</label>
                <select value={form.registered_owner_marital_regime} onChange={e => fld('registered_owner_marital_regime', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(MARITAL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Existing Bond Holder</label>
                <input value={form.existing_bond_holder} onChange={e => fld('existing_bond_holder', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bond Amount (BWP)</label>
                <input type="number" step="0.01" value={form.existing_bond_amount} onChange={e => fld('existing_bond_amount', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bond Reference</label>
                <input value={form.existing_bond_reference} onChange={e => fld('existing_bond_reference', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>

              {[['Servitudes', 'servitudes'], ['Caveats', 'caveats'], ['Notes', 'notes']].map(([label, key]) => (
                <div key={key} className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                  <textarea value={form[key]} onChange={e => fld(key, e.target.value)}
                    rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
                </div>
              ))}

              <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
